import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { ComponentProps, useMemo } from 'react';

import TimedOrCategorySearchCard from '@/components/GridItemCard/Card/MultiCardOptions/TimedOrCategorySearchCard/TimedOrCategorySearchCard';
import FluidItemGrid from '@/components/GridItemCard/FluidItemGrid/FluidItemGrid';
import PageTitle from '@/components/PageTitle/PageTitle';
import PaginateSortFilter from '@/components/Pagination/PaginateSortFilter';
import { itemSortOrders } from '@/enums';
import { useAppSelector } from '@/redux/hooks';
import { getIsLoadingStorefront, getStorefrontAgentName } from '@/redux/modules/storefront/storefront.selectors';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { addImageParams, getAuctioneerLogoUrl, imagePlaceholder } from '@/utils/urls';

import StorefrontPageHeaderMeta from './StorefrontPageHeaderMeta';

type Props = {
    buyNow: boolean;
    changePaginationPageSize: Function;
    containsBuyNowItems: boolean;
    filteredItemCount: number;
    isSearch: boolean;
    items: any[];
    logoId: string;
    pageSize: number;
    sellerId: number;
    sortOrder: string;
    totalItemCount: number;
    viewData: any;
};

const StorefrontPage = ({
    buyNow,
    changePaginationPageSize,
    containsBuyNowItems,
    filteredItemCount,
    isSearch,
    items,
    logoId,
    pageSize,
    sellerId,
    sortOrder,
    totalItemCount,
    viewData,
}: Props) => {
    const formatMessage = useAppFormatMessage();

    const { page, searchTerms } = viewData;

    const filteredCount = isSearch ? filteredItemCount : totalItemCount;

    const logoUrl = logoId ? getAuctioneerLogoUrl(logoId) : imagePlaceholder;
    const src = addImageParams({ height: 83, url: logoUrl });
    const agentName = useAppSelector(getStorefrontAgentName);
    const isLoading = useAppSelector((state) => getIsLoadingStorefront(state, sellerId));

    const pageTitle = useMemo((): ComponentProps<typeof PageTitle>['title'] => {
        if (agentName) {
            return agentName;
        }

        // no name, show sellerId title instead
        return (
            <FormattedMessage
                id="seller.number"
                values={{ sellerId }}
            />
        );
    }, [sellerId, agentName]);

    return (
        <div className="my-md flex flex-[1_0_auto] flex-col">
            <StorefrontPageHeaderMeta
                firstItemId={items?.[0]?.itemId}
                itemCount={items.length}
                sellerId={sellerId}
            />
            <div className="mb-md flex w-[calc(100%-40px)] max-w-[1200px] flex-row self-center">
                {logoId && (
                    <div className="h-[83px] flex-none border-r border-surface-100 pr-sm [&_img]:h-full">
                        <img
                            // marked as decorative since the description
                            // would be similar to the page title,
                            // which will always be read out
                            alt=""
                            role="presentation"
                            src={src}
                        />
                    </div>
                )}
                <div className="ml-lg flex flex-auto items-center justify-start [&_h1]:!m-0">
                    <PageTitle title={pageTitle} />
                </div>
            </div>
            <PaginateSortFilter
                buyNow={buyNow}
                buyNowFilterAvailable={containsBuyNowItems}
                changePaginationPageSize={changePaginationPageSize}
                className="mx-md mt-0 mb-lg w-[calc(100%-40px)]"
                empty={totalItemCount === 0}
                emptyMessage={<FormattedMessage id={isLoading ? 'seller.notLoaded' : 'seller.empty'} />}
                filteredItemCount={filteredCount}
                page={page}
                pageSize={pageSize}
                searchTerms={searchTerms}
                sortOptions={itemSortOrders}
                sortOrder={sortOrder}
                totalItemsCount={totalItemCount}
                type={String(formatMessage(['lots']))}
            >
                <FluidItemGrid
                    itemCardOption={TimedOrCategorySearchCard}
                    itemIds={items.map((item) => item.itemId)}
                />
            </PaginateSortFilter>
        </div>
    );
};

export default StorefrontPage;
